import api from './axios';
import { AxiosError } from 'axios';

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export enum TenantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

export interface Tenant {
  id: string;
  name: string;
  theme: string;
  config: Record<string, any>;
  status: TenantStatus;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logoPath?: string;
  faviconPath?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTenantDto {
  name: string;
  theme?: string;
  config?: Record<string, any>;
  status?: TenantStatus;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logoPath?: string;
  faviconPath?: string;
}

export interface UpdateTenantDto {
  name?: string;
  theme?: string;
  config?: Record<string, any>;
  status?: TenantStatus;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logoPath?: string;
  faviconPath?: string;
}

// Type guard to check if an object is a Tenant
function isTenant(obj: any): obj is Tenant {
  return obj && typeof obj === 'object' && 'id' in obj && 'name' in obj && 'status' in obj;
}

export const fetchTenants = async (): Promise<Tenant[]> => {
  try {
    const response = await api.get<BackendResponse<Tenant[]>>('/tenants');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid tenants response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching tenants:', error);
    throw error;
  }
};

export const fetchActiveTenants = async (): Promise<Tenant[]> => {
  try {
    const response = await api.get<BackendResponse<Tenant[]>>('/tenants/active');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid active tenants response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching active tenants:', error);
    throw error;
  }
};

export const fetchTenant = async (id: string): Promise<Tenant> => {
  try {
    const response = await api.get<BackendResponse<Tenant>>(`/tenants/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Tenant object
    if (isTenant(responseData)) {
      return responseData;
    }
    
    console.error('Invalid tenant response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error fetching tenant ${id}:`, error);
    throw error;
  }
};

export const createTenant = async (tenant: CreateTenantDto): Promise<Tenant> => {
  try {
    const response = await api.post<BackendResponse<Tenant>>('/tenants', tenant);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Tenant object
    if (isTenant(responseData)) {
      return responseData;
    }
    
    console.error('Invalid create tenant response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error creating tenant:', error);
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data;
      if (data.errors && Array.isArray(data.errors)) {
        throw new Error(data.errors.join(', '));
      } else if (data.message) {
        throw new Error(data.message);
      }
    }
    throw error;
  }
};

export const updateTenant = async (id: string, tenant: UpdateTenantDto): Promise<Tenant> => {
  try {
    const response = await api.patch<BackendResponse<Tenant>>(`/tenants/${id}`, tenant);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Tenant object
    if (isTenant(responseData)) {
      return responseData;
    }
    
    console.error('Invalid update tenant response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error updating tenant ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data;
      if (data.errors && Array.isArray(data.errors)) {
        throw new Error(data.errors.join(', '));
      } else if (data.message) {
        throw new Error(data.message);
      }
    }
    throw error;
  }
};

export const uploadTenantLogo = async (tenantId: string, formData: FormData): Promise<Tenant> => {
  const response = await api.post(`/tenants/${tenantId}/upload-logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadTenantFavicon = async (tenantId: string, formData: FormData): Promise<Tenant> => {
  const response = await api.post(`/tenants/${tenantId}/upload-favicon`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getTenantLogoUrl = async (tenantId: string): Promise<{ url: string }> => {
  const response = await api.get(`/tenants/${tenantId}/logo-url`);
  return response.data;
};

export const getTenantFaviconUrl = async (tenantId: string): Promise<{ url: string }> => {
  const response = await api.get(`/tenants/${tenantId}/favicon-url`);
  return response.data;
};

export const enableTenant = async (id: string): Promise<Tenant> => {
  try {
    const response = await api.patch<BackendResponse<Tenant>>(`/tenants/${id}/enable`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Tenant object
    if (isTenant(responseData)) {
      return responseData;
    }
    
    console.error('Invalid enable tenant response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error enabling tenant ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const disableTenant = async (id: string): Promise<Tenant> => {
  try {
    const response = await api.patch<BackendResponse<Tenant>>(`/tenants/${id}/disable`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Tenant object
    if (isTenant(responseData)) {
      return responseData;
    }
    
    console.error('Invalid disable tenant response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error disabling tenant ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const suspendTenant = async (id: string): Promise<Tenant> => {
  try {
    const response = await api.patch<BackendResponse<Tenant>>(`/tenants/${id}/suspend`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Tenant object
    if (isTenant(responseData)) {
      return responseData;
    }
    
    console.error('Invalid suspend tenant response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error suspending tenant ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const deleteTenant = async (id: string): Promise<void> => {
  try {
    await api.delete(`/tenants/${id}`);
  } catch (error) {
    console.error(`Error deleting tenant ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const getTenantStats = async (id: string): Promise<any> => {
  try {
    const response = await api.get(`/tenants/${id}/stats`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching tenant stats ${id}:`, error);
    throw error;
  }
};

export const statusLabels: Record<TenantStatus, string> = {
  [TenantStatus.ACTIVE]: 'Active',
  [TenantStatus.INACTIVE]: 'Inactive',
  [TenantStatus.SUSPENDED]: 'Suspended',
}; 